import React from 'react';

import Autocomplete from '@liquidweb-dev/undercurrent/Autocomplete';
import Box from '@liquidweb-dev/undercurrent/Box';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import { useInput } from 'components/LWForm/LWForm';
import useAutoWidth from './useAutoWidth';

/** @typedef {import('@liquidweb-dev/undercurrent/TextField').TextFieldProps} TextFieldProps */
/** @typedef {import('./useAutoWidth').AutoWidthOptions} AutoWidthOptions */

/**
 * An autocomplete textfield component to be used within the context of LWForm
 * @param {Omit<import('@liquidweb-dev/undercurrent/Autocomplete').AutocompleteProps, 'renderInput'> & {
 * getValidationValue?: (value: any) => string | undefined;
 * helperText?: TextFieldProps['helperText'];
 * label?: TextFieldProps['label'];
 * name: TextFieldProps['name'];
 * placeholder?: TextFieldProps['placeholder'];
 * required?: TextFieldProps['required'];
 * textFieldProps?: TextFieldProps;
 * startAdornment?: React.ReactNode;
 * autoWidth?: boolean | AutoWidthOptions;
 * }} props
 */
const HookedAutocomplete = ({
	getValidationValue,
	helperText,
	label,
	name,
	onBlur,
	onChange,
	placeholder,
	required,
	textFieldProps = {},
	startAdornment,
	autoWidth = false,
	sx,
	getOptionLabel,
	...rest
}) => {
	const { value, onValueChange, error, validateSimple } = useInput(name);

	// Auto-width hook
	const {
		textMeasureRef,
		inputRef,
		sx: autoWidthSx,
		isEnabled: autoWidthEnabled,
	} = useAutoWidth({
		value,
		autoWidth,
		placeholder,
		label,
		getOptionLabel,
	});

	// Merge auto-width styles with provided sx
	const mergedSx = autoWidthEnabled
		? (theme) => {
				const baseSx = typeof sx === 'function' ? sx(theme) : sx;
				return {
					...autoWidthSx,
					...baseSx,
				};
			}
		: sx;

	if (!name) {
		return null;
	}

	const validationValue =
		getValidationValue && value ? getValidationValue(value) : value;

	const handleChange = (event, newValue) => {
		if (!event.target) {
			return;
		}

		if (onChange) {
			onChange(event, newValue);
		}

		onValueChange(newValue);

		// validate on input change only if there is an existing error
		if (!error) {
			return;
		}

		const newValidationValue = getValidationValue
			? getValidationValue(newValue)
			: newValue;

		validateSimple(newValidationValue, required);
	};

	/** @param {React.FocusEvent<HTMLInputElement, Element>} event */
	const handleBlur = (event) => {
		if (!event.target) {
			return;
		}

		if (onBlur) {
			onBlur(event);
		}

		validateSimple(validationValue, required);
	};

	return (
		<>
			<Autocomplete
				ref={inputRef}
				id={`hooked-autocomplete--${name}`}
				value={value}
				onChange={handleChange}
				onBlur={handleBlur}
				getOptionLabel={getOptionLabel}
				sx={mergedSx}
				{...rest}
				renderInput={(params) => (
					<TextField
						{...params}
						slotProps={{
							...params.slotProps,
							input: {
								...params.slotProps?.input,
								startAdornment,
							},
						}}
						helperText={error || helperText}
						label={label}
						name={name}
						placeholder={placeholder || label}
						required={required}
						{...textFieldProps}
						error={Boolean(error)}
					/>
				)}
			/>
			{/* Hidden span to measure text width for autoWidth feature */}
			{autoWidthEnabled && (
				<Box
					component="span"
					ref={textMeasureRef}
					sx={{
						position: 'absolute',
						visibility: 'hidden',
						whiteSpace: 'nowrap',
						font: 'inherit',
					}}
					aria-hidden
				/>
			)}
		</>
	);
};

export default HookedAutocomplete;
